import { useEffect, useState } from 'react';
import config from '../../config';

/* eslint-disable no-undef */
// chrome is a global variable provided by Chrome browser
/**
 * Custom hook to detect if the ScreenApp Chrome extension is installed
 *
 * Uses chrome.runtime.sendMessage to verify the extension is installed and active.
 * This is the recommended approach per Chrome Extension best practices.
 *
 * @returns {boolean} True if the extension is installed and responsive, false otherwise
 */
export function useExtensionDetection(): boolean {
  // Lazy initial state: read from sessionStorage cache to prevent flash on remount
  const [isInstalled, setIsInstalled] = useState(() => {
    try {
      const cached = sessionStorage.getItem('chrome_ext_installed');
      return cached !== null ? JSON.parse(cached) : false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const checkExtension = () => {
      // Disable logs in production for performance
      const isDev = process.env.NODE_ENV === 'development';

      if (isDev) {
        console.log('[Extension Detection] Starting check...');
        console.log(
          '[Extension Detection] Looking for extension ID:',
          config.extensionId,
        );
      }

      // Check if chrome.runtime API is available
      // Note: chrome.runtime is only available if an extension with externally_connectable
      // matching this domain is installed
      if (typeof chrome === 'undefined' || !chrome.runtime) {
        if (isDev)
          console.log('[Extension Detection] chrome.runtime not available');
        setIsInstalled(false);
        sessionStorage.setItem('chrome_ext_installed', 'false');
        return;
      }

      if (isDev)
        console.log(
          '[Extension Detection] chrome.runtime is available, sending ping...',
        );

      try {
        // Send a ping message to the specific ScreenApp extension
        chrome.runtime.sendMessage(
          config.extensionId,
          { action: 'ping', source: 'webapp' },
          (response: any) => {
            // Check for chrome.runtime.lastError
            // This will be set if the extension is not installed or not responding
            if (chrome.runtime.lastError) {
              // Extension not installed or not responding
              if (isDev)
                console.log(
                  '[Extension Detection] Extension not responding:',
                  chrome.runtime.lastError.message,
                );
              setIsInstalled(false);
              sessionStorage.setItem('chrome_ext_installed', 'false');
            } else {
              // Extension responded successfully
              if (isDev)
                console.log(
                  '[Extension Detection] Extension responded:',
                  response,
                );
              setIsInstalled(true);
              sessionStorage.setItem('chrome_ext_installed', 'true');
            }
          },
        );
      } catch (error) {
        // If any error occurs, assume extension is not installed
        if (isDev) console.error('[Extension Detection] Error:', error);
        setIsInstalled(false);
        sessionStorage.setItem('chrome_ext_installed', 'false');
      }
    };

    // Initial check
    checkExtension();

    // Periodic check every 30 seconds to detect if extension is installed/uninstalled
    // This ensures the UI updates if the user installs or removes the extension
    const intervalId = setInterval(checkExtension, 30000);

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  return isInstalled;
}
